import { Fragment, useEffect, useState } from "react";
import axios from "../../config/axios";
import styles from "./CustomerOrders.module.scss";
import {
  Button,
  Card,
  Col,
  Popover,
  Rate,
  Row,
  Select,
  Spin,
  Table,
  Tabs,
  Tag,
} from "antd";
import Layout from "../../Components/Layout/Layout";
import BreadCrumb from "../../Components/BreadCrumb/BreadCrumb";
import { Link } from "react-router-dom";
import moment from "../../config/moment";
import CustomerLayout from "../../Components/CustomerLayout/CustomerLayout";
import RefundModal, {
  canRequestRefund,
  refundStatusTag,
} from "../../Components/RefundModal/RefundModal";

const CustomerOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("1");
  const [refundOrder, setRefundOrder] = useState(null);

  // the refund answer carries the order with its items' new statuses
  const onRefunded = (updated) =>
    setOrders((current) =>
      current.map((order) => (order.id === updated.id ? updated : order))
    );

  // set tab header based on active tab
  const setTabHeader = (activeTab) => {
    switch (activeTab) {
      case "1":
        return "Upcoming Orders";
      case "2":
        return "Past Orders";
      case "3":
        return "Cancelled Orders";
      default:
        return orders.length > 1 ? "Upcoming Orders" : "Orders";
    }
  };

  useEffect(() => {
    (async () => {
      const res = await axios.get("/orders/filter/users", {
        withCredentials: true,
      });
      setOrders(res.data);
      setLoading(false);
    })();
  }, []);

  return (
    <>
      {/*mobile version*/}
      <Row>
        <Col xs={24} lg={0}>
          <Layout>
            <div className={styles.orders_header}>
              <BreadCrumb
                list={[
                  { name: "Home", link: "/" },
                  {
                    name: setTabHeader(activeTab),
                    link: `/customer/orders`,
                  },
                ]}
              />
            </div>

            {loading ? (
              <div className={styles.orders_loading}>
                <Spin />
              </div>
            ) : (
              <>
                {orders.length > 0 ? (
                  <div className={styles.orders_section}>
                    <div className="custom_coupon_tab">
                      <Tabs
                        defaultActiveKey="1"
                        tabPosition="top"
                        animated={{
                          inkBar: true,
                          tabPane: true,
                        }}
                        onChange={(key) => setActiveTab(key)}
                      >
                        <Tabs.TabPane tab="Upcoming" key="1">
                          <UpcomingOrders orders={orders} onRefund={setRefundOrder} />
                        </Tabs.TabPane>
                        <Tabs.TabPane tab="Past Orders" key="2">
                          <PastOrders orders={orders} onRefund={setRefundOrder} />
                        </Tabs.TabPane>
                        <Tabs.TabPane tab="Cancelled Orders" key="3">
                          <CancelledOrders orders={orders} onRefund={setRefundOrder} />
                        </Tabs.TabPane>
                      </Tabs>
                    </div>
                  </div>
                ) : (
                  <div className={styles.no_orders}>
                    <img
                      src="https://desi-fresh.b-cdn.net/components_static/Account/no_data.svg"
                      alt="no_data"
                    />

                    <p>
                      <strong>No Order Yet</strong>
                    </p>

                    <p>Browse products and start order.</p>

                    <Link to={"/"}>
                      <Button type="primary">Start Shopping</Button>
                    </Link>
                  </div>
                )}
              </>
            )}
          </Layout>
        </Col>
      </Row>

      {/*desktop version*/}
      <Row>
        <Col xs={0} lg={24}>
          <CustomerLayout
            breadcrumb={[
              { name: "Back to Home", link: "/" },
              {
                name: "My Orders",
                link: `/customer/orders`,
              },
              {
                name: setTabHeader(activeTab),
                link: `/customer/orders`,
              },
            ]}
          >
            <Row>
              <Col span={24}>
                <div className={styles.my_order_dt_top}>
                  <img
                    src="https://desi-fresh.b-cdn.net/misc/Orders.svg"
                    alt="orders"
                  />
                  <h2>My Orders</h2>
                </div>
              </Col>
            </Row>
            {loading ? (
              <div className={styles.orders_loading}>
                <Spin />
              </div>
            ) : (
              <>
                {orders.length > 0 ? (
                  <div className={styles.orders_section}>
                    <div className="custom_coupon_tab">
                      <Tabs
                        defaultActiveKey="1"
                        tabPosition="top"
                        animated={{
                          inkBar: true,
                          tabPane: true,
                        }}
                        onChange={(key) => setActiveTab(key)}
                      >
                        <Tabs.TabPane tab="Upcoming" key="1">
                          <UpcomingOrders orders={orders} onRefund={setRefundOrder} />
                        </Tabs.TabPane>
                        <Tabs.TabPane tab="Past Orders" key="2">
                          <PastOrders orders={orders} onRefund={setRefundOrder} />
                        </Tabs.TabPane>
                        <Tabs.TabPane tab="Cancelled Orders" key="3">
                          <CancelledOrders orders={orders} onRefund={setRefundOrder} />
                        </Tabs.TabPane>
                      </Tabs>
                    </div>
                  </div>
                ) : (
                  <div className={styles.no_orders}>
                    <img
                      src="https://desi-fresh.b-cdn.net/components_static/Account/no_data.svg"
                      alt="no_data"
                    />

                    <p>
                      <strong>No Order Yet</strong>
                    </p>

                    <p>Browse products and start order.</p>

                    <Link to={"/"}>
                      <Button type="primary">Start Shopping</Button>
                    </Link>
                  </div>
                )}
              </>
            )}
          </CustomerLayout>
        </Col>
      </Row>

      {refundOrder && (
        <RefundModal
          order={refundOrder}
          open
          onClose={() => setRefundOrder(null)}
          onRefunded={onRefunded}
        />
      )}
    </>
  );
};

export default CustomerOrders;

// an order is past once delivered, cancelled orders get their own tab and
// anything else is still on its way to the customer
const PAST_STATUSES = ["delivered"];
const CANCELLED_STATUSES = ["cancelled"];

const isCancelledOrder = (order) => CANCELLED_STATUSES.includes(order.status);
const isPastOrder = (order) => PAST_STATUSES.includes(order.status);
const isUpcomingOrder = (order) => !isCancelledOrder(order) && !isPastOrder(order);

// the order's most pressing refund state, shown next to its delivery status
const REFUND_TAG_PRIORITY = ["refund_requested", "refund_accepted", "refund_rejected"];

const orderRefundTag = (order) => {
  const statuses = order.order_items.map((item) => item.status);
  const status = REFUND_TAG_PRIORITY.find((candidate) => statuses.includes(candidate));
  return status ? refundStatusTag(status) : null;
};

const RefundButton = ({ order, onRefund }) =>
  canRequestRefund(order) ? (
    <Button size="small" onClick={() => onRefund(order)}>
      Request refund
    </Button>
  ) : null;

const ALL_MONTHS = "all";
const PAGE_SIZE = 10;

const stepperSteps = [
  {
    title: "Order packed",
    image:
      "https://desi-fresh.b-cdn.net/components_static/Account/order_packed.svg",
  },
  {
    title: "On the way",
    image:
      "https://desi-fresh.b-cdn.net/components_static/Account/On%20the%20way.svg",
  },
  {
    title: "Delivered",
    image: "https://desi-fresh.b-cdn.net/components_static/Account/track.svg",
  },
];

const orderColumns = (onRefund) => [
  {
    title: "Order ID",
    dataIndex: "id",
    key: "id",
  },
  {
    title: "Date",
    dataIndex: "timestamp",
    key: "timestamp",
    render: (timestamp) => (
      <div>{moment(timestamp).format("DD.MM.YYYY")}</div>
    ),
  },
  {
    title: "Products",
    dataIndex: "order_items",
    key: "order_items",
    render: (order_items) => (
      <div
        style={{
          display: "flex",
          gap: 5,
        }}
      >
        {order_items.map((item, index) => (
          <img
            key={index}
            src={item.image}
            alt="order_item"
            height={40}
            style={{
              objectFit: "cover",
            }}
          />
        ))}
      </div>
    ),
  },
  {
    title: "Price",
    dataIndex: "total",
    key: "total",
    render: (total) => <div>{total} €</div>,
  },
  {
    title: "Delivery Status",
    dataIndex: "status",
    key: "status",
    render: (status, order) => (
      <>
        <Tag
          color={"#F2F4F8"}
          style={{
            color: "#003171",
            textTransform: "capitalize",
          }}
        >
          {status}
        </Tag>
        {orderRefundTag(order)}
      </>
    ),
  },
  {
    title: "Refund",
    key: "refund",
    render: (_, order) => <RefundButton order={order} onRefund={onRefund} />,
  },
  {
    title: "Rating",
    dataIndex: "rating",
    key: "rating",
    render: (rating) => <Rate />,
  },
  {
    title: () => (
      <img
        className={styles.three_dot}
        src="https://desi-fresh.b-cdn.net/components_static/Account/three_dot_horiz.svg"
        alt="dot"
      />
    ),
    dataIndex: "action",
    key: "action",
    render: () => (
      <Popover
        content={orderPopover}
        placement="bottomRight"
        trigger="click"
        overlayClassName="custom_order_popover"
      >
        <img
          className={styles.three_dot}
          src="https://desi-fresh.b-cdn.net/components_static/Account/three_dot_horiz.svg"
          alt="dot"
        />
      </Popover>
    ),
  },
];

// one tab of the orders page, the orders handed in are already the ones that
// belong to the tab and only get narrowed down to the picked month here
const OrdersTab = ({ orders, showStepper, isCancelled, emptyText, onRefund }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [month, setMonth] = useState(ALL_MONTHS);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  // the months the customer actually ordered in, newest first
  const monthOptions = [
    { value: ALL_MONTHS, label: "All months" },
    ...[...new Set(orders.map((order) => moment(order.timestamp).format("YYYY-MM")))]
      .sort()
      .reverse()
      .map((value) => ({
        value,
        label: moment(value, "YYYY-MM").format("MMMM YYYY"),
      })),
  ];

  const filteredOrders =
    month === ALL_MONTHS
      ? orders
      : orders.filter(
          (order) => moment(order.timestamp).format("YYYY-MM") === month
        );

  return (
    <div className={styles.upcoming_orders}>
      <div className={styles.upcoming_orders_top}>
        <Select
          value={month}
          style={{
            width: 160,
          }}
          onChange={(value) => {
            setMonth(value);
            setVisibleCount(PAGE_SIZE);
          }}
          options={monthOptions}
        />

        <div>
          <strong>
            {filteredOrders.length}{" "}
            {filteredOrders.length === 1 ? "order" : "orders"}
          </strong>
        </div>
      </div>

      <div>
        {showStepper && (
          <Stepper
            steps={stepperSteps}
            isCancelled={isCancelled}
            activeStep={activeStep}
            setActiveStep={setActiveStep}
          />
        )}

        <br />

        <Row>
          <Col xs={24} lg={0}>
            <>
              {filteredOrders.length === 0 && (
                <p style={{ textAlign: "center" }}>{emptyText}</p>
              )}
              {filteredOrders.slice(0, visibleCount).map((order) => (
                <OrderCard order={order} key={order.id} onRefund={onRefund} />
              ))}
              {filteredOrders.length > visibleCount && (
                <div className={styles.load_more}>
                  <Button
                    onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}
                  >
                    Load More
                  </Button>
                </div>
              )}
            </>
          </Col>

          <Col xs={0} lg={24}>
            {/*  desktop*/}
            <Table
              rowKey="id"
              columns={orderColumns(onRefund)}
              dataSource={filteredOrders}
              locale={{ emptyText }}
            />
          </Col>
        </Row>
      </div>
    </div>
  );
};

const UpcomingOrders = ({ orders, onRefund }) => (
  <OrdersTab
    onRefund={onRefund}
    orders={orders.filter(isUpcomingOrder)}
    showStepper
    isCancelled={false}
    emptyText="You have no upcoming orders."
  />
);

const PastOrders = ({ orders, onRefund }) => (
  <OrdersTab
    onRefund={onRefund}
    orders={orders.filter(isPastOrder)}
    emptyText="You have no past orders."
  />
);

const CancelledOrders = ({ orders, onRefund }) => (
  <OrdersTab
    onRefund={onRefund}
    orders={orders.filter(isCancelledOrder)}
    showStepper
    isCancelled
    emptyText="You have no cancelled orders."
  />
);

const Stepper = ({ steps, activeStep, setActiveStep, isCancelled }) => {
  return (
    <Fragment>
      <div className={styles.stepper}>
        {steps.map((step, index) => (
          <div
            className={styles.step}
            key={index}
            onClick={() => setActiveStep(index)}
          >
            <img src={step.image} alt={step.title} />
            <div style={{ fontWeight: "600" }}>{step.title}</div>
          </div>
        ))}
      </div>
      <div className={styles.stepper_dots}>
        <div
          className={
            activeStep === 0 || activeStep === 1 || activeStep === 2
              ? `${styles.dot_circle} ${styles.dot_circle_active}`
              : styles.dot_circle
          }
        />
        <div
          style={isCancelled ? { backgroundColor: "#CA2027" } : null}
          className={
            activeStep === 1 || activeStep === 2
              ? `${styles.line_1} ${styles.line_1_active}`
              : styles.line_1
          }
        />
        <div
          style={isCancelled ? { border: "2px solid #CA2027" } : null}
          className={
            activeStep === 1 || activeStep === 2
              ? `${styles.dot_circle} ${styles.dot_circle_active}`
              : styles.dot_circle
          }
        />
        <div
          style={isCancelled ? { backgroundColor: "#CA2027" } : null}
          className={
            activeStep === 2 || activeStep === 3
              ? `${styles.line_2} ${styles.line_2_active}`
              : styles.line_2
          }
        />
        <div
          style={isCancelled ? { border: "2px solid #CA2027" } : null}
          className={
            activeStep === 2
              ? `${styles.dot_circle} ${styles.dot_circle_active}`
              : styles.dot_circle
          }
        />
      </div>
    </Fragment>
  );
};

const OrderCard = ({ order, onRefund }) => {
  return (
    <Card
      bordered={false}
      className={styles.order_card}
      bodyStyle={{ padding: 10 }}
    >
      <Row gutter={[10, 10]}>
        <Col span={22}>
          <Row>
            <Col span={12}>
              <Fragment>
                <strong> Order Id: </strong>
                <strong> #{order.id} </strong>
              </Fragment>
              <Fragment>
                <strong> Date: </strong>
                <span> {moment(order.timestamp).format("DD.MM.YYYY")} </span>
              </Fragment>
            </Col>
            <Col span={12}>
              <div className={styles.delivery_stat_w}>
                <strong> Delivery Status </strong>
                <div className={styles.order_stat}>{order.status}</div>
                {orderRefundTag(order)}
              </div>
            </Col>
          </Row>

          <Row>
            <Col span={24}>
              <div className={styles.order_items}>
                {order.order_items.slice(0, 5).map((item, index) => (
                  <img src={item.image} key={index} alt={index} />
                ))}
              </div>
            </Col>
          </Row>

          <Row>
            <Col span={14}>
              <Fragment>
                <div>
                  <strong>Rating</strong>
                </div>
                <Rate />
              </Fragment>
            </Col>
            <Col span={10}>
              <div className={styles.order_total}>Price : {order.total}€</div>
            </Col>
          </Row>

          {canRequestRefund(order) && (
            <Row>
              <Col span={24} className={styles.order_refund}>
                <RefundButton order={order} onRefund={onRefund} />
              </Col>
            </Row>
          )}
        </Col>
        <Col span={2}>
          <Popover
            content={orderPopover}
            placement="bottomRight"
            trigger="click"
            overlayClassName="custom_order_popover"
          >
            <img
              className={styles.three_dot}
              src="https://desi-fresh.b-cdn.net/components_static/Account/three_dot_horiz.svg"
              alt="dot"
            />
          </Popover>
        </Col>
      </Row>
    </Card>
  );
};

const popoverData = [
  {
    title: "View Order",
    image: "https://desi-fresh.b-cdn.net/components_static/Account/eye.svg",
  },
  {
    title: "Tracking",
    image:
      "https://desi-fresh.b-cdn.net/components_static/Account/tracking.svg",
  },
  {
    title: "Invoice",
    image: "https://desi-fresh.b-cdn.net/components_static/Account/invoice.svg",
  },
];

const orderPopover = (
  <div className={styles.order_popover}>
    {popoverData.map((item, index) => (
      <div className={styles.order_popover_item} key={index}>
        <img src={item.image} alt={item.title} />
        <div>{item.title}</div>
      </div>
    ))}
  </div>
);
